import React from "react";
import { Link, useLocation } from "react-router-dom";
import PLogo from "./PLogo";
import PriceSplash from "./PriceSplash";

/**
 * Price — Site officiel (pricecovoit.fr)
 * Layout partagé Header + Footer pour les pages marketing.
 * Réutilise les utilities .street-* et la palette de l'app mobile
 * pour garder la même identité visuelle.
 */

function Logo({ compact = false }) {
  return (
    <Link to="/" aria-label="Price — accueil" className="inline-flex items-center gap-2.5">
      <PLogo size={compact ? 38 : 44} />
      <div className="leading-none">
        <div className="font-[Outfit] text-[22px] font-black tracking-tight" style={{ color: "#1E0910" }}>
          Price
        </div>
        <div className="mt-0.5 text-[8.5px] font-black uppercase tracking-[0.22em]" style={{ color: "rgba(30,9,16,0.55)" }}>
          Covoiturage
        </div>
      </div>
    </Link>
  );
}

export function PriceSiteHeader() {
  const { pathname } = useLocation();
  return (
    <header
      className="sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-[#FFF6EE]/85"
      style={{ borderBottom: "3px solid #1E0910", background: "#FFF6EE" }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
        <Logo />
        <nav className="hidden md:flex items-center gap-1">
          <a href="/#comment" className="rounded-full px-4 py-2 text-sm font-bold hover:bg-[#FFE9D6] transition-colors">
            Comment ça marche
          </a>
          <a href="/#faq" className="rounded-full px-4 py-2 text-sm font-bold hover:bg-[#FFE9D6] transition-colors">
            FAQ
          </a>
        </nav>
        {pathname !== "/download" && (
          <Link
            to="/download"
            data-testid="site-header-download"
            className="street-press inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-black uppercase tracking-widest text-white street-border"
            style={{ background: "#F86A78", boxShadow: "4px 4px 0 0 #1E0910" }}
          >
            Télécharger
          </Link>
        )}
      </div>
    </header>
  );
}

export function PriceSiteFooter() {
  return (
    <footer
      className="mt-24"
      style={{ background: "#1E0910", color: "#FFF6EE", borderTop: "3px solid #1E0910" }}
    >
      <div className="mx-auto max-w-6xl px-5 py-14">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <PLogo size={40} />
              <div className="font-[Outfit] text-3xl font-black" style={{ color: "#F86A78" }}>Price</div>
            </div>
            <p className="mt-4 max-w-sm text-sm opacity-70">
              Le covoiturage instantané du quotidien. Parce qu&apos;un trajet coûte moins quand il se partage.
            </p>
            <div className="mt-6 flex gap-3">
              <a
                href="https://www.instagram.com/pricecovoit"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram Price Covoiturage"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:!bg-[#F86A78]"
                style={{ border: "2px solid rgba(255,246,238,0.4)" }}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
                  <path d="M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 1.9.2 2.4.4.6.2 1 .5 1.5 1s.8.9 1 1.5c.2.5.3 1.2.4 2.4.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 1.2-.2 1.9-.4 2.4a4 4 0 0 1-1 1.5c-.5.5-.9.8-1.5 1-.5.2-1.2.3-2.4.4-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2-.1-1.9-.2-2.4-.4a4 4 0 0 1-1.5-1 4 4 0 0 1-1-1.5c-.2-.5-.3-1.2-.4-2.4C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8c.1-1.2.2-1.9.4-2.4.2-.6.5-1 1-1.5s.9-.8 1.5-1c.5-.2 1.2-.3 2.4-.4C8.4 2.2 8.8 2.2 12 2.2Zm0 8.8a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm0 8.2a3.2 3.2 0 1 1 0-6.4 3.2 3.2 0 0 1 0 6.4Zm5.2-8.4a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Z" />
                </svg>
              </a>
            </div>
          </div>

          <div>
            <div className="text-xs font-black uppercase tracking-widest" style={{ color: "#F86A78" }}>Navigation</div>
            <ul className="mt-4 space-y-2 text-sm opacity-90">
              <li><Link to="/" className="hover:opacity-100 hover:text-[#F86A78] transition-colors">Accueil</Link></li>
              <li><Link to="/download" className="hover:text-[#F86A78] transition-colors">Télécharger l&apos;app</Link></li>
              <li><a href="/#comment" className="hover:text-[#F86A78] transition-colors">Comment ça marche</a></li>
              <li><a href="/#faq" className="hover:text-[#F86A78] transition-colors">FAQ</a></li>
            </ul>
          </div>

          <div>
            <div className="text-xs font-black uppercase tracking-widest" style={{ color: "#F86A78" }}>Légal</div>
            <ul className="mt-4 space-y-2 text-sm opacity-90">
              <li><a href="mailto:contact@pricecovoit.fr" className="hover:text-[#F86A78] transition-colors">Contact</a></li>
              <li><Link to="/confidentialite" className="hover:text-[#F86A78] transition-colors">Politique de confidentialité</Link></li>
              <li><Link to="/cgu" className="hover:text-[#F86A78] transition-colors">Conditions générales</Link></li>
              <li>
                <a
                  href="/admin"
                  data-testid="footer-admin-link"
                  className="hover:text-[#F86A78] transition-colors"
                >
                  Coin administrateur
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div
          className="mt-10 flex flex-col items-start justify-between gap-3 pt-6 text-xs opacity-60 md:flex-row md:items-center"
          style={{ borderTop: "1px solid rgba(255,246,238,0.15)" }}
        >
          <div>© {new Date().getFullYear()} Price Covoiturage. Tous droits réservés.</div>
          <div>Fait avec ❤️ en France</div>
        </div>
      </div>
    </footer>
  );
}

/** Wrapper commun pour toutes les pages du site pricecovoit.fr. */
export default function PriceSiteLayout({ children, hideSplash = false }) {
  return (
    <div style={{ background: "#FFF6EE", minHeight: "100vh" }}>
      {!hideSplash && <PriceSplash />}
      <PriceSiteHeader />
      {children}
      <PriceSiteFooter />
    </div>
  );
}
